import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getLogs } from '../api/client'

const LINE_OPTIONS = [50, 200, 500, 1000]
const REFRESH_INTERVAL_MS = 5000

function levelClass(line: string): string {
  if (line.includes('[ERROR]')) return 'text-red-400'
  if (line.includes('[WARNING]')) return 'text-yellow-400'
  if (line.includes('[DEBUG]')) return 'text-gray-500'
  return 'text-gray-300'
}

function levelBadge(line: string): string {
  if (line.includes('[ERROR]')) return 'ERROR'
  if (line.includes('[WARNING]')) return 'WARN'
  if (line.includes('[DEBUG]')) return 'DEBUG'
  if (line.includes('[INFO]')) return 'INFO'
  return ''
}

const LS_LINE_COUNT = 'logviewer:lineCount'
const LS_AUTO_REFRESH = 'logviewer:autoRefresh'

function readLineCount(): number {
  const v = parseInt(localStorage.getItem(LS_LINE_COUNT) ?? '', 10)
  return LINE_OPTIONS.includes(v) ? v : 200
}

function readAutoRefresh(): boolean {
  return localStorage.getItem(LS_AUTO_REFRESH) === 'true'
}

export function LogViewer() {
  const { t } = useTranslation()
  const [lines, setLines] = useState<string[]>([])
  const [lineCount, setLineCount] = useState<number>(readLineCount)
  const [autoRefresh, setAutoRefresh] = useState<boolean>(readAutoRefresh)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight
    }
  }

  const load = async (doScroll = false) => {
    try {
      const data = await getLogs(lineCount)
      setLines(data.lines)
      setLastUpdated(new Date())
      setError(null)
      if (doScroll) {
        setTimeout(scrollToBottom, 50)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('log_viewer.load_error'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setLoading(true)
    load(true)
  }, [lineCount])

  useEffect(() => {
    if (!autoRefresh) return
    const id = setInterval(() => load(true), REFRESH_INTERVAL_MS)
    return () => clearInterval(id)
  }, [autoRefresh, lineCount])

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-3">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap shrink-0">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500 dark:text-gray-400">{t('log_viewer.lines_label')}</span>
          {LINE_OPTIONS.map((n) => (
            <button
              key={n}
              onClick={() => { setLineCount(n); localStorage.setItem(LS_LINE_COUNT, String(n)) }}
              className={`text-xs px-2 py-1 rounded font-medium transition-colors ${
                lineCount === n
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {n}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => { setAutoRefresh(e.target.checked); localStorage.setItem(LS_AUTO_REFRESH, String(e.target.checked)) }}
            className="rounded"
          />
          {t('log_viewer.auto_refresh_label')}
        </label>

        <button
          onClick={() => load(true)}
          className="text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1 rounded font-medium transition-colors"
        >
          {t('log_viewer.refresh_button')}
        </button>

        {lastUpdated && (
          <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
            {t('log_viewer.last_updated', { time: lastUpdated.toLocaleTimeString('nl-NL') })}
            {autoRefresh && <span className="ml-1 text-green-500">{t('log_viewer.live_indicator')}</span>}
          </span>
        )}
      </div>

      {/* Log output */}
      <div
        ref={scrollContainerRef}
        className="flex-1 min-h-0 bg-gray-950 rounded-xl border border-gray-800 overflow-auto"
      >
        {loading ? (
          <div className="flex items-center justify-center h-full text-gray-500 text-sm">{t('log_viewer.loading')}</div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-red-400 text-sm">{error}</div>
        ) : lines.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-600 text-sm">{t('log_viewer.empty_state')}</div>
        ) : (
          <div className="p-4 font-mono text-xs leading-5 space-y-px">
            {lines.map((line, i) => {
              const badge = levelBadge(line)
              return (
                <div key={i} className={`flex gap-2 ${levelClass(line)}`}>
                  {badge && (
                    <span className={`shrink-0 w-12 text-center text-[10px] font-bold px-1 rounded self-start mt-0.5 ${
                      badge === 'ERROR' ? 'bg-red-900 text-red-300' :
                      badge === 'WARN'  ? 'bg-yellow-900 text-yellow-300' :
                      badge === 'DEBUG' ? 'bg-gray-800 text-gray-500' :
                                          'bg-gray-800 text-gray-400'
                    }`}>
                      {badge}
                    </span>
                  )}
                  <span className="break-all whitespace-pre-wrap">{line}</span>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <p className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
        {t('log_viewer.footer_note', { lineCount })}
      </p>
    </div>
  )
}
